/**
 * Referral Reports time-bucket chart.
 *
 * One datum per time bucket (see lib/reportChart), one <Line> per
 * series. Because no two data points share an x-position anymore,
 * every point owns its own hit area — the "tooltip only fires on the
 * first node" defect came from the old row-per-point data, which
 * stacked many points at each repeated x.
 *
 * `width`/`height` are for tests only (ResponsiveContainer measures 0
 * in jsdom); production renders responsively.
 */
import React from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from "recharts";
import { formatBucketTick, tickInterval, SERIES_COLORS } from "../lib/reportChart";

export const ReportTimeChart = ({ chart, formatValue, width, height }) => {
  const graph = (
    <LineChart data={chart.data} width={width} height={height}
               margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="bucket"
        interval={tickInterval(chart.data.length)}
        tickFormatter={(v) => formatBucketTick(v, chart.grain, chart.withYear)}
      />
      <YAxis allowDecimals={false} />
      <Tooltip
        labelFormatter={(v) => formatBucketTick(v, chart.grain, true)}
        formatter={(v, k) => [formatValue(v, chart.measureIdFor(k)), chart.legendName(k)]}
      />
      <Legend formatter={(k) => chart.legendName(k)} />
      {chart.series.map((s, i) => (
        <Line
          key={s}
          type="monotone"
          dataKey={s}
          name={chart.legendName(s)}
          stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
          strokeWidth={2}
          dot={{ r: 2 }}
          activeDot={{ r: 5 }}
          isAnimationActive={false}
        />
      ))}
    </LineChart>
  );
  if (width) return graph;
  return (
    <div className="h-64" data-testid="chart-graph">
      <ResponsiveContainer width="100%" height="100%">{graph}</ResponsiveContainer>
    </div>
  );
};

export default ReportTimeChart;
